import {
  FileTextOutlined,
  InfoCircleOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { AccountingSubType } from "@entities/accounting/lib/constants";
import { FirebaseService, ToastService } from "@shared/lib/services";
import { CircularProgress, Input, Upload } from "@shared/ui";
import { RadioInput } from "@shared/ui/RadioInput";
import { useState } from "react";
import { BILLING_TYPES } from "../model/types";
import styles from "./BillingSummaryForm.module.scss";

const STORAGE_PATH = "brand-accounting";
const ACCEPT_TYPES = ".pdf,.jpg,.jpeg,.png";
const MAX_SIZE_MB = 5;

interface BillingSummaryFormProps {
  billingType: AccountingSubType;
  onBillingTypeChange: (type: AccountingSubType) => void;
  invoiceNumber: string;
  onInvoiceNumberChange: (value: string) => void;
  invoiceDate: string;
  onInvoiceDateChange: (value: string) => void;
  invoiceAmount: string;
  onInvoiceAmountChange: (value: string) => void;
  uploadedFile: File | null;
  onFileChange: (file: File | null) => void;
  onUploadSuccess?: (url: string) => void;
  grandTotal: number;
  amountError?: string;
}

export function BillingSummaryForm({
  billingType,
  onBillingTypeChange,
  invoiceNumber,
  onInvoiceNumberChange,
  invoiceDate,
  onInvoiceDateChange,
  invoiceAmount,
  onInvoiceAmountChange,
  uploadedFile,
  onFileChange,
  onUploadSuccess,
  grandTotal,
  amountError,
}: BillingSummaryFormProps) {
  const [isUploading, setIsUploading] = useState(false);

  const expectedTotal = grandTotal;
  const parsedAmount = Number(invoiceAmount);
  const showAmountError =
    amountError ||
    (invoiceAmount !== "" &&
      !Number.isNaN(parsedAmount) &&
      parsedAmount !== expectedTotal);

  const handleBeforeUpload = (file: File): boolean => {
    const isLt5M = file.size / 1024 / 1024 < MAX_SIZE_MB;
    if (!isLt5M) {
      ToastService.error(`File must be smaller than ${MAX_SIZE_MB}MB!`);
      return false;
    }
    setIsUploading(true);
    FirebaseService.uploadFileToFirebase(
      `${STORAGE_PATH}/${billingType}/${file.name}${Date.now()}`,
      file
    )
      .then((url) => {
        onFileChange(file);
        onUploadSuccess?.(url);
        ToastService.success("File uploaded successfully");
      })
      .catch(() => {
        ToastService.error("File upload failed. Please try again.");
      })
      .finally(() => {
        setIsUploading(false);
      });
    return false; // Prevent default upload, we handle via Firebase
  };

  return (
    <div className={styles.form}>
      <div className={styles.section}>
        <label className={styles.label}>Select Type</label>
        <div className={styles.radioGroup}>
          {BILLING_TYPES.map((type) => (
            <RadioInput
              key={type}
              isSelected={billingType === type}
              handleSelect={() => onBillingTypeChange(type)}
            >
              {type}
            </RadioInput>
          ))}
        </div>
      </div>

      <div className={styles.infoBox}>
        <InfoCircleOutlined className={styles.infoIcon} />
        <span className={styles.infoText}>
          Payment Terms: <strong>Net 30 Days</strong>
        </span>
      </div>

      <div className={styles.tipBox}>
        <InfoCircleOutlined className={styles.tipIcon} />
        <p className={styles.tipText}>
          Tip: Upload the invoice first to verify details.
        </p>
      </div>

      <div className={styles.section}>
        <label className={styles.label}>Upload {billingType}</label>
        <Upload.Dragger
          name="file"
          multiple={false}
          accept={ACCEPT_TYPES}
          showUploadList={false}
          beforeUpload={handleBeforeUpload}
          disabled={isUploading}
          className={styles.uploadDragger}
        >
          <div
            className={`${styles.uploadContent} ${
              uploadedFile ? styles.uploadContentFilled : ""
            } ${isUploading ? styles.uploadContentLoading : ""}`}
          >
            {isUploading ? (
              <>
                <CircularProgress size="small" />
                <span className={styles.uploadPlaceholder}>Uploading...</span>
              </>
            ) : uploadedFile ? (
              <>
                <FileTextOutlined className={styles.uploadIconUploaded} />
                <span className={styles.fileName}>{uploadedFile.name}</span>
                <span className={styles.fileSize}>
                  {(uploadedFile.size / 1024).toFixed(1)} KB
                </span>
              </>
            ) : (
              <>
                <UploadOutlined className={styles.uploadIcon} />
                <span className={styles.uploadPlaceholder}>
                  Click or drag to upload {billingType.toLowerCase()}
                </span>
                <span className={styles.uploadHint}>
                  PDF, JPG or PNG (Max {MAX_SIZE_MB}MB)
                </span>
              </>
            )}
          </div>
        </Upload.Dragger>
      </div>

      <div className={styles.row}>
        <div className={styles.field}>
          <label className={styles.label}>{billingType} Number</label>
          <Input
            value={invoiceNumber}
            onChange={(e) => onInvoiceNumberChange(e.target.value)}
            placeholder={
              billingType === "Invoice" ? "INV-2024-001" : "DN-2024-001"
            }
            className={styles.input}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{billingType} Date</label>
          <Input
            type="date"
            value={invoiceDate}
            onChange={(e) => {
              onInvoiceDateChange(e.target.value);
            }}
            className={styles.input}
          />
        </div>
      </div>

      <div className={styles.section}>
        <label className={styles.label}>Total Amount (Incl. Tax)</label>
        <div className={styles.amountWrap}>
          <span className={styles.currency}>₹</span>
          <Input
            type="text"
            inputMode="decimal"
            value={invoiceAmount}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                onInvoiceAmountChange("");
                return;
              }
              const digitsAndDot = raw.replace(/[^\d.]/g, "");
              const dotIndex = digitsAndDot.indexOf(".");
              const normalized =
                dotIndex === -1
                  ? digitsAndDot
                  : `${digitsAndDot.slice(0, dotIndex + 1)}${digitsAndDot.slice(dotIndex + 1).replace(/\./g, "")}`;
              onInvoiceAmountChange(normalized);
            }}
            placeholder="0.00"
            className={styles.amountInput}
          />
        </div>
        {showAmountError && (
          <p className={styles.amountError}>
            {amountError ??
              `Amount must match grand total (₹${expectedTotal.toLocaleString()})`}
          </p>
        )}
      </div>
    </div>
  );
}
